// store/slices/subscriptionSlice.js
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { subscriptionService } from "../../services/subscription.service";
import { subscriptionPlans } from "../../config/subscriptionPlans";

export const subscriptionStates = {
    PENDING_ADMIN: 'pending_admin',
    PENDING_PAYMENT: 'pending_payment',
    PAYMENT_SUBMITTED: 'payment_submitted',
    PAYMENT_VERIFIED: 'payment_verified',
    ACTIVE: 'active',
    REJECTED: 'rejected',
    EXPIRED: 'expired'
};

export const fetchSubscriptionStatus = createAsyncThunk(
    'subscription/fetchStatus',
    async (userId) => {
        const [subscription, pendingRequest] = await Promise.all([
            subscriptionService.getUserSubscription(userId),
            subscriptionService.getUserRequest(userId)
        ]);
        return { subscription, pendingRequest };
    }
);

export const requestSubscription = createAsyncThunk(
    'subscription/requestSubscription',
    async ({ userId, planData }) => {
        const requestId = await subscriptionService.requestSubscription(userId, planData);
        const request = await subscriptionService.getUserRequest(userId);
        return request;
    }
);

const subscriptionSlice = createSlice({
    name: "subscription",
    initialState: {
        data: null,
        pendingRequest: null,
        plans: subscriptionPlans,
        loading: false,
        error: null,
    },
    reducers: {
        setPendingRequest: (state, action) => {
            state.pendingRequest = action.payload;
        },
        clearPendingRequest: (state) => {
            state.pendingRequest = null;
        },
        resetSubscriptionError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchSubscriptionStatus.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchSubscriptionStatus.fulfilled, (state, action) => {
                state.loading = false;
                state.data = action.payload.subscription;
                state.pendingRequest = action.payload.pendingRequest;
                state.error = null;
            })
            .addCase(fetchSubscriptionStatus.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            })
            .addCase(requestSubscription.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(requestSubscription.fulfilled, (state, action) => {
                state.loading = false;
                state.pendingRequest = action.payload;
                state.error = null;
            })
            .addCase(requestSubscription.rejected, (state, action) => {
                state.loading = false;
                state.error = action.error.message;
            });
    },
});

export const { setPendingRequest, clearPendingRequest, resetSubscriptionError } = subscriptionSlice.actions;

// Selectors
export const selectUserSubscription = (state) => state.subscription.data;
export const selectPendingRequest = (state) => state.subscription.pendingRequest;
export const selectSubscriptionLoading = (state) => state.subscription.loading;
export const selectSubscriptionError = (state) => state.subscription.error;

export default subscriptionSlice.reducer;